import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { 
  Building2, 
  User, 
  Shield,
  Save,
  Loader2,
  ExternalLink,
  Link2,
  Copy,
  Trash2,
  AlertTriangle,
  Upload,
  Image,
  X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { user, organization, subscription, refreshUserData, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(organization?.logo_url || null);
  const fileInputRef = useRef(null);
  
  const [orgData, setOrgData] = useState({
    name: organization?.name || "",
    google_place_id: organization?.google_place_id || "",
    initial_google_rating: organization?.initial_google_rating || "",
    initial_review_count: organization?.initial_review_count || ""
  });

  // Update orgData when organization changes
  useEffect(() => {
    if (organization) {
      setOrgData({
        name: organization.name || "",
        google_place_id: organization.google_place_id || "",
        initial_google_rating: organization.initial_google_rating || "",
        initial_review_count: organization.initial_review_count || ""
      });
      setLogoPreview(organization.logo_url || null);
    }
  }, [organization]);

  const handleOrgChange = (e) => {
    const { name, value } = e.target;
    setOrgData(prev => ({ ...prev, [name]: value }));
  };

  // Logo upload handling
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Format non supporté. Utilisez JPG, PNG, GIF ou WebP");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("L'image est trop volumineuse (max 2MB)");
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/organizations/me/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setLogoPreview(response.data.logo_url);
      await refreshUserData();
      toast.success("Logo mis à jour !");
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    setUploadingLogo(true);
    try {
      await axios.delete(`${API}/organizations/me/logo`);
      setLogoPreview(null);
      await refreshUserData();
      toast.success("Logo supprimé");
    } catch (error) {
      console.error("Error removing logo:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setUploadingLogo(false);
    }
  };

  // Generate Google URL from Place ID
  const googleBusinessUrl = orgData.google_place_id 
    ? `https://search.google.com/local/writereview?placeid=${orgData.google_place_id.trim()}`
    : "";

  const handleSaveOrg = async () => {
    if (!orgData.google_place_id) {
      toast.error("Le Place ID Google est obligatoire");
      return;
    }

    // Validate rating
    const rating = parseFloat(orgData.initial_google_rating);
    if (orgData.initial_google_rating && (isNaN(rating) || rating < 0 || rating > 5)) {
      toast.error("La note doit être entre 0 et 5");
      return;
    }

    // Validate review count
    const reviewCount = parseInt(orgData.initial_review_count);
    if (orgData.initial_review_count && (isNaN(reviewCount) || reviewCount < 0)) {
      toast.error("Le nombre d'avis doit être positif");
      return;
    }

    setLoading(true);
    try {
      // Send the generated URL along with the Place ID
      await axios.put(`${API}/organizations/me`, {
        ...orgData,
        google_business_url: googleBusinessUrl
      });
      await refreshUserData();
      toast.success("Paramètres mis à jour !");
    } catch (error) {
      console.error("Error updating organization:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!googleBusinessUrl) return;
    try {
      await navigator.clipboard.writeText(googleBusinessUrl);
      toast.success("Lien copié !");
    } catch (error) {
      toast.error("Erreur lors de la copie");
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== "SUPPRIMER") {
      toast.error("Veuillez taper 'SUPPRIMER' pour confirmer");
      return;
    }
    if (!deletePassword) {
      toast.error("Veuillez entrer votre mot de passe");
      return;
    }

    setDeleting(true);
    try {
      await axios.post(`${API}/auth/delete-account`, {
        password: deletePassword,
        confirm_text: deleteConfirm
      });
      toast.success("Compte supprimé avec succès");
      logout();
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
          Paramètres
        </h1>
        <p className="text-slate-500 mt-1">
          Configuration de votre QR Code et redirection Google
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* QR Code Settings */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Paramètres du QR Code</h2>
                <p className="text-sm text-slate-500">Ces informations sont utilisées pour la génération de votre QR Code</p>
              </div>
            </div>

            <div className="space-y-5">
              {/* Organization Logo */}
              <div className="space-y-2">
                <Label>Logo de l'établissement</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Ce logo sera affiché sur la page de collecte d'avis
                </p>
                <div className="flex items-start gap-4">
                  {/* Logo Preview */}
                  <div className="relative">
                    {logoPreview ? (
                      <div className="relative">
                        <img 
                          src={logoPreview} 
                          alt="Logo" 
                          className="w-20 h-20 object-cover rounded-xl border border-slate-200"
                        />
                        <button
                          onClick={handleRemoveLogo}
                          disabled={uploadingLogo}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                          data-testid="remove-logo-btn"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 bg-slate-100 rounded-xl flex items-center justify-center border border-dashed border-slate-300">
                        <Image className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Upload Button */}
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="rounded-xl"
                      data-testid="upload-logo-btn"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Upload...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {logoPreview ? "Changer le logo" : "Ajouter un logo"}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-400 mt-2">
                      JPG, PNG, GIF ou WebP. Max 2MB.
                    </p>
                  </div>
                </div>
              </div>

              {/* Organization Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Nom de l'établissement</Label>
                <Input
                  id="name"
                  name="name"
                  value={orgData.name}
                  onChange={handleOrgChange}
                  className="h-11 rounded-xl"
                  placeholder="Ex: Restaurant Le Gourmet"
                  data-testid="settings-org-name"
                />
              </div>

              {/* Google Place ID */}
              <div className="space-y-2">
                <Label htmlFor="google_place_id">
                  Google Place ID <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="google_place_id"
                    name="google_place_id"
                    value={orgData.google_place_id}
                    onChange={handleOrgChange}
                    className="h-11 rounded-xl flex-1"
                    placeholder="Ex: ChIJN1t_tDeuEmsRUsoyG83frY4"
                    data-testid="settings-place-id"
                  />
                  {googleBusinessUrl && (
                    <Button
                      variant="outline"
                      onClick={handleCopyUrl}
                      className="rounded-xl px-3"
                      title="Copier le lien Google"
                      data-testid="copy-url-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <a 
                  href="https://developers.google.com/maps/documentation/places/web-service/place-id#find-id"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-violet-600 hover:text-violet-700 hover:underline"
                >
                  <Link2 className="w-3 h-3" />
                  Comment trouver mon Place ID ?
                </a>
                {googleBusinessUrl && (
                  <p className="text-xs text-slate-500 mt-2">
                    Lien de redirection : <span className="text-slate-700">{googleBusinessUrl}</span>
                  </p>
                )}
              </div>

              {/* Initial Google Stats */}
              <div className="space-y-4 pt-4 border-t border-slate-100">
                <div>
                  <h3 className="text-sm font-medium text-slate-900 mb-1">Statistiques Google actuelles</h3>
                  <p className="text-xs text-slate-500">
                    Renseignez votre note et nombre d'avis actuels pour suivre votre progression
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="initial_google_rating">Note actuelle (sur 5)</Label>
                    <Input
                      id="initial_google_rating"
                      name="initial_google_rating"
                      type="number"
                      step="0.1"
                      min="0"
                      max="5"
                      value={orgData.initial_google_rating}
                      onChange={handleOrgChange}
                      className="h-11 rounded-xl"
                      placeholder="Ex: 4.2"
                      data-testid="settings-initial-rating"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="initial_review_count">Nombre d'avis actuels</Label>
                    <Input
                      id="initial_review_count"
                      name="initial_review_count"
                      type="number"
                      min="0"
                      value={orgData.initial_review_count}
                      onChange={handleOrgChange}
                      className="h-11 rounded-xl"
                      placeholder="Ex: 47"
                      data-testid="settings-initial-reviews"
                    />
                  </div>
                </div>
                
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
                  <p className="text-xs text-amber-800">
                    <strong>Astuce :</strong> Ces données vous permettront de mesurer l'impact de ResponsIA sur votre e-réputation dans le tableau de bord KPIs.
                  </p>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
                <p className="text-sm text-violet-800 font-medium mb-2">Comment ça fonctionne ?</p>
                <ol className="text-sm text-violet-700 space-y-1 list-decimal list-inside">
                  <li>Le client scanne votre QR Code</li>
                  <li>Il note son expérience de 1 à 5 étoiles</li>
                  <li>Si 4+ étoiles → Redirection vers le lien Google ci-dessus</li>
                  <li>Si moins de 4 étoiles → L'avis reste privé</li>
                </ol>
              </div>

              <Button
                onClick={handleSaveOrg}
                disabled={loading}
                className="rounded-full bg-slate-900 hover:bg-slate-800"
                data-testid="save-settings-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Account Info */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-violet-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Compte</h2>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm text-slate-500">Nom</p>
                <p className="font-medium text-slate-900">
                  {user?.first_name} {user?.last_name}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium text-slate-900">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Subscription Info */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-violet-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Abonnement</h2>
            </div>

            {subscription ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Plan</span>
                  <span className="font-medium capitalize text-violet-600">
                    {subscription.plan}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Prix</span>
                  <span className="font-medium text-slate-900">{subscription.price}€/mois</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Statut</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    subscription.status === "trial" 
                      ? "bg-amber-100 text-amber-700"
                      : "bg-emerald-100 text-emerald-700"
                  }`}>
                    {subscription.status === "trial" ? "Essai" : "Actif"}
                  </span>
                </div>
                {subscription.status === "trial" && subscription.trial_ends_at && (
                  <div className="pt-2 border-t border-slate-100">
                    <p className="text-xs text-slate-500">
                      Fin de l'essai: {new Date(subscription.trial_ends_at).toLocaleDateString("fr-FR")}
                    </p>
                  </div>
                )}
                <Button 
                  variant="outline" 
                  className="rounded-full w-full mt-2"
                  onClick={() => window.location.href = "/subscription"}
                >
                  Gérer mon abonnement
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-slate-500 mb-3">Aucun abonnement actif</p>
                <Button 
                  variant="outline" 
                  className="rounded-full"
                  onClick={() => window.location.href = "/subscription"}
                >
                  Voir les offres
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>

          {/* Danger Zone */}
          <div className="bg-white rounded-2xl p-6 border border-red-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-red-900">Zone dangereuse</h2>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              La suppression de votre compte est irréversible. Toutes vos données seront définitivement effacées.
            </p>

            <Button 
              variant="outline" 
              className="rounded-full w-full border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => setShowDeleteModal(true)}
              data-testid="delete-account-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer mon compte
            </Button>
          </div>
        </div>
      </div>
    </div>

    {/* Delete Account Modal */}
    {showDeleteModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Supprimer le compte</h3>
              <p className="text-sm text-slate-500">Cette action est irréversible</p>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-red-800">
              <strong>Attention :</strong> Toutes vos données seront supprimées définitivement :
            </p>
            <ul className="text-sm text-red-700 mt-2 space-y-1">
              <li>• Votre établissement et son QR code</li>
              <li>• Tous vos avis collectés</li>
              <li>• Votre abonnement (annulé immédiatement)</li>
              <li>• Votre compte utilisateur</li>
            </ul>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="delete-password">Mot de passe</Label>
              <Input
                id="delete-password"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Entrez votre mot de passe"
                className="h-11 rounded-xl"
                data-testid="delete-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="delete-confirm">
                Tapez <span className="font-bold text-red-600">SUPPRIMER</span> pour confirmer
              </Label>
              <Input
                id="delete-confirm"
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="SUPPRIMER"
                className="h-11 rounded-xl"
                data-testid="delete-confirm-input"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              className="flex-1 rounded-full"
              onClick={() => {
                setShowDeleteModal(false);
                setDeletePassword("");
                setDeleteConfirm("");
              }}
              disabled={deleting}
            >
              Annuler
            </Button>
            <Button
              className="flex-1 rounded-full bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirm !== "SUPPRIMER" || !deletePassword}
              data-testid="confirm-delete-button"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer définitivement
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Settings;
